/**
 * Helper utility to convert between US and UK/AU/NZ spelling conventions
 */

// Common spelling differences between US and UK/AU/NZ English
const usToUkSpellingMap: Record<string, string> = {
  // -ize to -ise
  'ize': 'ise',
  'izing': 'ising',
  'ization': 'isation',
  
  // -or to -our
  'color': 'colour',
  'flavor': 'flavour',
  'honor': 'honour',
  'humor': 'humour',
  'labor': 'labour',
  'neighbor': 'neighbour',
  
  // -er to -re
  'center': 'centre',
  'theater': 'theatre',
  'meter': 'metre',
  'fiber': 'fibre',
  
  // -og to -ogue
  'catalog': 'catalogue',
  'dialog': 'dialogue',
  
  // Other common differences
  'gray': 'grey',
  'traveling': 'travelling',
  'traveled': 'travelled',
  'traveler': 'traveller',
  'fulfillment': 'fulfilment',
  'enrollment': 'enrolment',
  'defense': 'defence',
  'license': 'licence',
  'practice': 'practise', // When used as a verb
  'program': 'programme', // For TV, not computer programs
  'specialized': 'specialised', // Add specific words that might be common
  'Specialized': 'Specialised', // Capitalized version
  'optimized': 'optimised',
  'authorized': 'authorised',
  'customize': 'customise',
  'personalized': 'personalised',
  'prioritized': 'prioritised',
  'utilized': 'utilised',
  'standardized': 'standardised',
  'localized': 'localised',
  'mobilized': 'mobilised',
  'maximized': 'maximised',
  'revolutionized': 'revolutionised',
  'digitized': 'digitised',
  'modernized': 'modernised',
  'harmonized': 'harmonised',
  'monetized': 'monetised',
  
  // Compound words with AI, ML, etc.
  'AI-optimized': 'AI-optimised',
  'ML-optimized': 'ML-optimised',
  'AI-personalized': 'AI-personalised',
  'ML-personalized': 'ML-personalised',
  'AI-prioritized': 'AI-prioritised',
  'ML-prioritized': 'ML-prioritised',
  'AI-specialized': 'AI-specialised',
  'ML-specialized': 'ML-specialised',
  'cloud-optimized': 'cloud-optimised',
  'purpose-optimized': 'purpose-optimised',
  'custom-optimized': 'custom-optimised',
  
  // Add specific word variants that need direct mapping
  'visualization': 'visualisation',
  'visualizations': 'visualisations',
  'personalization': 'personalisation',
  'optimization': 'optimisation',
  'optimizations': 'optimisations',
  'prioritization': 'prioritisation',
  'utilization': 'utilisation',
  'standardization': 'standardisation',
  'localization': 'localisation',
  'mobilization': 'mobilisation',
  'maximization': 'maximisation',
  'revolutionization': 'revolutionisation',
  'digitization': 'digitisation',
  'modernization': 'modernisation',
  'harmonization': 'harmonisation',
  'monetization': 'monetisation',
};

// Create the reverse mapping for UK/AU/NZ to US
const ukToUsSpellingMap: Record<string, string> = {};
Object.entries(usToUkSpellingMap).forEach(([us, uk]) => {
  ukToUsSpellingMap[uk] = us;
});

// Common -ize/-ization words that should be converted to -ise/-isation
const commonIzeWords = [
  'specialize', 'specialized', 'specializing', 'specialization',
  'optimize', 'optimized', 'optimizing', 'optimization',
  'authorize', 'authorized', 'authorizing', 'authorization',
  'customize', 'customized', 'customizing', 'customization',
  'organize', 'organized', 'organizing', 'organization',
  'recognize', 'recognized', 'recognizing', 'recognition',
  'realize', 'realized', 'realizing', 'realization',
  'visualize', 'visualized', 'visualizing', 'visualization',
  'utilize', 'utilized', 'utilizing', 'utilization',
  'personalize', 'personalized', 'personalizing', 'personalization',
  'prioritize', 'prioritized', 'prioritizing', 'prioritization',
  'standardize', 'standardized', 'standardizing', 'standardization',
  'localize', 'localized', 'localizing', 'localization',
  'mobilize', 'mobilized', 'mobilizing', 'mobilization',
  'maximize', 'maximized', 'maximizing', 'maximization',
  'revolutionize', 'revolutionized', 'revolutionizing', 'revolutionization',
  'digitize', 'digitized', 'digitizing', 'digitization',
  'modernize', 'modernized', 'modernizing', 'modernization',
  'harmonize', 'harmonized', 'harmonizing', 'harmonization',
  'monetize', 'monetized', 'monetizing', 'monetization',
];

/**
 * Converts text from US English spelling to UK/AU/NZ English spelling
 */
export function convertToUkSpelling(text: string): string {
  if (!text) return text;
  let result = text;
  
  // Handle compound words with hyphens first (AI-optimized, etc.)
  Object.entries(usToUkSpellingMap).forEach(([us, uk]) => {
    if (us.includes('-')) {
      const regex = new RegExp(`\\b${us.replace(/-/g, '\\-')}\\b`, 'gi');
      result = result.replace(regex, uk);
    }
  });
  
  // Replace specific words next - preserving capitalization
  Object.entries(usToUkSpellingMap).forEach(([us, uk]) => {
    if (!us.includes('-')) { // Skip hyphenated words we've already handled
      // Use word boundary to avoid replacing parts of words
      const regex = new RegExp(`\\b${us}\\b`, 'g');
      result = result.replace(regex, uk);
      
      // Also handle case where first letter is capitalized
      const capitalizedUs = us.charAt(0).toUpperCase() + us.slice(1);
      const capitalizedUk = uk.charAt(0).toUpperCase() + uk.slice(1);
      const capitalizedRegex = new RegExp(`\\b${capitalizedUs}\\b`, 'g');
      result = result.replace(capitalizedRegex, capitalizedUk);
    }
  });
  
  // Handle common -ize/-ization words with proper capitalization
  commonIzeWords.forEach(word => {
    const iseForm = word.replace(/ize(d|s)?$/, 'ise$1').replace(/izing$/, 'ising').replace(/ization$/, 'isation');
    
    // Handle lowercase version
    const regex = new RegExp(`\\b${word}\\b`, 'g');
    result = result.replace(regex, iseForm);
    
    // Handle capitalized version
    const capitalizedWord = word.charAt(0).toUpperCase() + word.slice(1);
    const capitalizedIseForm = iseForm.charAt(0).toUpperCase() + iseForm.slice(1);
    const capitalizedRegex = new RegExp(`\\b${capitalizedWord}\\b`, 'g');
    result = result.replace(capitalizedRegex, capitalizedIseForm);
  });
  
  // Handle general suffixes like -ize, -ization with proper capitalization
  result = result.replace(/(\w+)ize\b/g, (match, prefix) => {
    return prefix + 'ise';
  });
  result = result.replace(/(\w+)ized\b/g, (match, prefix) => {
    return prefix + 'ised';
  });
  result = result.replace(/(\w+)izes\b/g, (match, prefix) => {
    return prefix + 'ises';
  });
  result = result.replace(/(\w+)izing\b/g, (match, prefix) => {
    return prefix + 'ising';
  });
  result = result.replace(/(\w+)ization\b/g, (match, prefix) => {
    return prefix + 'isation';
  });
  result = result.replace(/(\w+)izations\b/g, (match, prefix) => {
    return prefix + 'isations';
  });
  
  return result;
}

/**
 * Converts text from UK/AU/NZ English spelling to US English spelling
 */
export function convertToUsSpelling(text: string): string {
  if (!text) return text;
  let result = text;
  
  // Handle compound words with hyphens first (AI-optimised, etc.)
  Object.entries(ukToUsSpellingMap).forEach(([uk, us]) => {
    if (uk.includes('-')) {
      const regex = new RegExp(`\\b${uk.replace(/-/g, '\\-')}\\b`, 'gi');
      result = result.replace(regex, us);
    }
  });
  
  // Replace specific words next - preserving capitalization
  Object.entries(ukToUsSpellingMap).forEach(([uk, us]) => {
    if (!uk.includes('-')) { // Skip hyphenated words we've already handled
      // Use word boundary to avoid replacing parts of words
      const regex = new RegExp(`\\b${uk}\\b`, 'g');
      result = result.replace(regex, us);
      
      // Also handle case where first letter is capitalized
      const capitalizedUk = uk.charAt(0).toUpperCase() + uk.slice(1);
      const capitalizedUs = us.charAt(0).toUpperCase() + us.slice(1);
      const capitalizedRegex = new RegExp(`\\b${capitalizedUk}\\b`, 'g');
      result = result.replace(capitalizedRegex, capitalizedUs);
    }
  });
  
  // Handle common -ise/-isation words (reverse of commonIzeWords) with proper capitalization
  commonIzeWords.forEach(word => {
    const iseForm = word.replace(/ize(d|s)?$/, 'ise$1').replace(/izing$/, 'ising').replace(/ization$/, 'isation');
    
    // Handle lowercase version
    const regex = new RegExp(`\\b${iseForm}\\b`, 'g');
    result = result.replace(regex, word);
    
    // Handle capitalized version
    const capitalizedIseForm = iseForm.charAt(0).toUpperCase() + iseForm.slice(1);
    const capitalizedWord = word.charAt(0).toUpperCase() + word.slice(1);
    const capitalizedRegex = new RegExp(`\\b${capitalizedIseForm}\\b`, 'g');
    result = result.replace(capitalizedRegex, capitalizedWord);
  });
  
  // Handle general suffixes like -ise, -isation with proper capitalization
  result = result.replace(/(\w+)ise\b/g, (match, prefix) => {
    return prefix + 'ize';
  });
  result = result.replace(/(\w+)ised\b/g, (match, prefix) => {
    return prefix + 'ized';
  });
  result = result.replace(/(\w+)ises\b/g, (match, prefix) => {
    return prefix + 'izes';
  });
  result = result.replace(/(\w+)ising\b/g, (match, prefix) => {
    return prefix + 'izing';
  });
  result = result.replace(/(\w+)isation\b/g, (match, prefix) => {
    return prefix + 'ization';
  });
  result = result.replace(/(\w+)isations\b/g, (match, prefix) => {
    return prefix + 'izations';
  });
  
  return result;
}

/**
 * Apply spelling conversion based on the target locale
 */
export function localizeSpelling(text: string, locale: string): string {
  if (!text) return text;
  
  // Australian and New Zealand English use UK spelling conventions
  if (locale === 'en-AU' || locale === 'en-NZ') {
    return convertToUkSpelling(text);
  }
  
  // Default to US spelling for 'en'
  if (locale === 'en') {
    return convertToUsSpelling(text);
  }
  
  // Return original text for other locales
  return text;
} 